import React, { useState, useEffect } from 'react';
import { Button, Input, message, Popconfirm, Space, Table, TableColumnsType, Tag } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  getKnowledgeBaseList,
  getItemList,
  deleteKnowledgeBase,
  deleteItem,
  KnowledgeBaseVo,
  KnowledgeItemVo,
  ITEM_STATUS_OPTIONS,
  ITEM_KIND_OPTIONS,
} from '@/api/knowledgeBase';
import { useUpdate } from '@/common/hook';
import EditKnowledgeBase from './editModal';
import EditItem from './editItemModal';
import ViewItem from './viewItemModal';
import TextUploadModal from './textUploadModal';
import TestQueryModal from './testQueryModal';
import ChunkModal from './chunkModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const kbColumns: TableColumnsType<KnowledgeBaseVo> = [
  { title: '知识库名称', dataIndex: 'name', key: 'name', width: 150 },
  { title: '描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
  { title: '分块大小', dataIndex: 'chunkSize', key: 'chunkSize', width: 80 },
  { title: '分块重叠', dataIndex: 'chunkOverlap', key: 'chunkOverlap', width: 80 },
  {
    title: '创建时间',
    dataIndex: 'createTime',
    key: 'createTime',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const itemColumns: TableColumnsType<KnowledgeItemVo> = [
  { title: '条目名称', dataIndex: 'name', key: 'name', ellipsis: true },
  { title: '格式', dataIndex: 'format', key: 'format', width: 80 },
  {
    title: '条目类型',
    dataIndex: 'kind',
    key: 'kind',
    width: 100,
    render: (kind: string) => {
      const option = ITEM_KIND_OPTIONS.find(o => o.value === (kind || 'rag'));
      return <Tag color={option?.color}>{option?.label}</Tag>;
    },
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 100,
    render: (status: number) => {
      const option = ITEM_STATUS_OPTIONS.find(o => o.value === status);
      return <Tag color={option?.color}>{option?.label}</Tag>;
    },
  },
  { title: '分块数', dataIndex: 'chunkCount', key: 'chunkCount', width: 80 },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const KnowledgeBaseList = () => {
  const [editData, setEditData] = useState<KnowledgeBaseVo>();
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [update, updateFn] = useUpdate();

  const [importVisible, setImportVisible] = useState<boolean>(false);
  const [currentKbId, setCurrentKbId] = useState<number>(0);

  const [testVisible, setTestVisible] = useState<boolean>(false);

  const [chunkVisible, setChunkVisible] = useState<boolean>(false);
  const [chunkItemId, setChunkItemId] = useState<number | null>(null);
  const [chunkItemName, setChunkItemName] = useState<string>('');

  const [editItemVisible, setEditItemVisible] = useState<boolean>(false);
  const [editItemData, setEditItemData] = useState<KnowledgeItemVo | undefined>();

  const [viewItemVisible, setViewItemVisible] = useState<boolean>(false);
  const [viewItemData, setViewItemData] = useState<KnowledgeItemVo | undefined>();

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [itemMap, setItemMap] = useState<Map<number, KnowledgeItemVo[]>>(new Map());

  const { data: kbList, loading } = useRequest(() => getKnowledgeBaseList({ keyword }), {
    refreshDeps: [update, keyword],
  });

  useEffect(() => {
    expandedKeys.forEach(key => {
      const kbId = Number(key);
      if (!itemMap.has(kbId)) {
        getItemList(kbId).then(list => {
          setItemMap(prev => new Map(prev).set(kbId, list || []));
        });
      }
    });
  }, [expandedKeys]);

  const onDeleteKb = (id: number) => {
    deleteKnowledgeBase(id).then(() => {
      message.success('删除成功');
      updateFn();
      setItemMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    });
  };

  const onDeleteItem = (kbId: number, itemId: number) => {
    deleteItem(itemId)
      .then(() => {
        message.success('删除成功');
        getItemList(kbId).then(list => {
          setItemMap(prev => new Map(prev).set(kbId, list || []));
        });
      })
      .catch((err) => {
        console.error('删除条目失败:', err);
        message.error('删除失败');
      });
  };

  const onViewChunks = (itemId: number, itemName: string) => {
    setChunkItemId(itemId);
    setChunkItemName(itemName);
    setChunkVisible(true);
  };

  const onEditItem = (item: KnowledgeItemVo) => {
    setEditItemData(item);
    setEditItemVisible(true);
  };

  const onViewItem = (item: KnowledgeItemVo) => {
    setViewItemData(item);
    setViewItemVisible(true);
  };

  const handleExpand = (expanded: boolean, record: KnowledgeBaseVo) => {
    if (expanded) {
      setExpandedKeys([...expandedKeys, record.id]);
    } else {
      setExpandedKeys(expandedKeys.filter(key => key !== record.id));
    }
  };

  const buildColumns = [
    ...kbColumns,
    {
      title: '操作',
      key: 'operation',
      width: 200,
      render: (_: any, record: KnowledgeBaseVo) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setCurrentKbId(record.id);
              setImportVisible(true);
            }}
          >
            添加内容
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setCurrentKbId(record.id);
              setTestVisible(true);
            }}
          >
            测试
          </Button>
          <Popconfirm title="确定删除该知识库及其所有条目？" onConfirm={() => onDeleteKb(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (kb: KnowledgeBaseVo) => {
    const itemList = itemMap.get(kb.id) || [];

    const itemActionColumns = [
      ...itemColumns,
      {
        title: '操作',
        key: 'operation',
        width: 220,
        render: (_: any, record: KnowledgeItemVo) => (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => onEditItem(record)}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => onViewItem(record)}
            >
              查看
            </Button>
            {record.kind !== 'config' && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => onViewChunks(record.id, record.name)}
              >
                查看分块
              </Button>
            )}
            <Popconfirm title="确定删除该条目？" onConfirm={() => onDeleteItem(kb.id, record.id)}>
              <Button type="link" size="small" style={{ padding: 0 }} danger>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div style={{ padding: '12px 24px', background: '#fafafa' }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>条目列表</div>
        <Table<KnowledgeItemVo>
          columns={itemActionColumns}
          dataSource={itemList}
          pagination={false}
          rowKey="id"
          size="small"
          loading={!itemMap.has(kb.id)}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增知识库
        </Button>
        <Input
          placeholder="搜索知识库名称"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<KnowledgeBaseVo>
        columns={buildColumns}
        dataSource={kbList ?? []}
        loading={loading}
        rowKey="id"
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedKeys,
          onExpand: handleExpand,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={e => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              style={{ cursor: 'pointer', padding: '4px 8px' }}
            >
              {expanded ? '▼' : '▶'}
            </span>
          ),
        }}
        onRow={(record) => ({
          onClick: () => {
            const isExpanded = expandedKeys.includes(record.id);
            handleExpand(!isExpanded, record);
          },
          style: { cursor: 'pointer' },
        })}
      />
      {editVisible && (
        <EditKnowledgeBase
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {importVisible && (
        <TextUploadModal
          kbId={currentKbId}
          visible={importVisible}
          onSuccess={() => {
            updateFn();
            getItemList(currentKbId).then(list => {
              setItemMap(prev => new Map(prev).set(currentKbId, list || []));
            });
          }}
          onCancel={() => setImportVisible(false)}
        />
      )}
      {testVisible && (
        <TestQueryModal
          kbId={currentKbId}
          updateFn={updateFn}
          onCancel={() => setTestVisible(false)}
        />
      )}
      {chunkVisible && (
        <ChunkModal
          visible={chunkVisible}
          itemId={chunkItemId}
          itemName={chunkItemName}
          onClose={() => setChunkVisible(false)}
        />
      )}
      {editItemVisible && editItemData && (
        <EditItem
          data={editItemData}
          updateFn={updateFn}
          onCancel={() => {
            setEditItemVisible(false);
            setEditItemData(undefined);
          }}
        />
      )}
      {viewItemVisible && viewItemData && (
        <ViewItem
          data={viewItemData}
          onClose={() => {
            setViewItemVisible(false);
            setViewItemData(undefined);
          }}
        />
      )}
    </>
  );
};

export default KnowledgeBaseList;
